import { Card, CardBody, Skeleton } from "@heroui/react";
import { useStats } from "@src/presentation/hooks/stats";
import { FileText, TrendingUp, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { useMemo } from "react";
import { usePermission } from "@src/presentation/hooks/usePermission";
import { PermissionPlaceholder } from "@src/presentation/components/PermissionPlaceholder";

export function PascoAnalyticsCard() {
    const { hasPermission } = usePermission();
    const canViewStats = hasPermission("stats:read");

    const { data: stats, isLoading } = useStats({
        enabled: canViewStats,
    });

    // Calculate analytics from stats data
    const analytics = useMemo(() => {
        if (!stats) {
            return {
                totalPascos: 0,
                completedPascos: 0,
                pendingPascos: 0,
                processingPascos: 0,
                failedJobs: 0,
                completionRate: 0,
                processingRate: 0,
                failureRate: 0,
            };
        }

        const totalPascos = stats.pasco.total_pascos;
        const completedPascos = stats.pasco.status_breakdown.completed || 0;
        const pendingPascos = stats.pasco.status_breakdown.pending || 0;
        const processingPascos = stats.pasco.status_breakdown.processing || 0;
        const failedJobs = stats.pasco_jobs.status_breakdown.failed || 0;
        const totalJobs = stats.pasco_jobs.total_jobs;

        return {
            totalPascos,
            completedPascos,
            pendingPascos,
            processingPascos,
            failedJobs,
            completionRate: totalPascos > 0 ? (completedPascos / totalPascos) * 100 : 0,
            processingRate: totalPascos > 0 ? (processingPascos / totalPascos) * 100 : 0,
            failureRate: totalJobs > 0 ? (failedJobs / totalJobs) * 100 : 0,
        };
    }, [stats]);

    // Early return if no permission
    if (!canViewStats) {
        return <PermissionPlaceholder title="PASCO Analytics" description="You need 'stats:read' permission to view PASCO analytics." />;
    }

    if (isLoading) {
        return (
            <Card className="border border-border rounded shadow-none">
                <CardBody className="p-6">
                    <Skeleton className="h-6 w-32 rounded mb-4" />
                    <div className="space-y-3">
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                    </div>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className="border border-border rounded shadow-none hover:shadow-custom transition-shadow">
            <CardBody className="p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="flex h-8 sm:h-10 w-8 sm:w-10 items-center justify-center rounded bg-primary/10">
                        <TrendingUp className="h-4 sm:h-5 w-4 sm:w-5 text-primary" />
                    </div>
                    <div>
                        <h2 className="text-base sm:text-lg font-semibold text-foreground">PASCO Analytics</h2>
                        <p className="text-xs sm:text-sm text-muted-foreground">Performance metrics and trends</p>
                    </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="p-2 sm:p-3 rounded border border-border bg-background">
                        <div className="flex items-center gap-1 sm:gap-2 mb-1">
                            <FileText className="h-3 sm:h-4 w-3 sm:w-4 text-primary" />
                            <p className="text-xs text-muted-foreground">Total PASCOs</p>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-primary">{analytics.totalPascos}</p>
                    </div>
                    <div className="p-2 sm:p-3 rounded border border-border bg-background">
                        <div className="flex items-center gap-1 sm:gap-2 mb-1">
                            <CheckCircle className="h-3 sm:h-4 w-3 sm:w-4 text-success" />
                            <p className="text-xs text-muted-foreground">Completed</p>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-success">{analytics.completedPascos}</p>
                    </div>
                </div>

                {/* Status Overview */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="p-2 sm:p-3 rounded border border-border bg-background text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <Clock className="h-3 sm:h-4 w-3 sm:w-4 text-warning" />
                        </div>
                        <p className="text-xs text-muted-foreground">Processing</p>
                        <p className="text-sm sm:text-base font-bold text-warning">{analytics.processingPascos}</p>
                    </div>
                    <div className="p-2 sm:p-3 rounded border border-border bg-background text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <FileText className="h-3 sm:h-4 w-3 sm:w-4 text-default-500" />
                        </div>
                        <p className="text-xs text-muted-foreground">Pending</p>
                        <p className="text-sm sm:text-base font-bold text-default-500">{analytics.pendingPascos}</p>
                    </div>
                    <div className="p-2 sm:p-3 rounded border border-border bg-background text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <AlertTriangle className="h-3 sm:h-4 w-3 sm:w-4 text-danger" />
                        </div>
                        <p className="text-xs text-muted-foreground">Failed Jobs</p>
                        <p className="text-sm sm:text-base font-bold text-danger">{analytics.failedJobs}</p>
                    </div>
                </div>

                {/* Progress Metrics */}
                <div className="space-y-3">
                    <div>
                        <div className="flex justify-between text-xs mb-1">
                            <div className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-success" />
                                <span className="text-muted-foreground">Completion Rate</span>
                            </div>
                            <span className="font-medium">{analytics.completionRate.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 bg-default-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-success rounded-full transition-all"
                                style={{ width: `${analytics.completionRate}%` }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="flex justify-between text-xs mb-1">
                            <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-warning" />
                                <span className="text-muted-foreground">Processing Rate</span>
                            </div>
                            <span className="font-medium">{analytics.processingRate.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 bg-default-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-warning rounded-full transition-all"
                                style={{ width: `${analytics.processingRate}%` }}
                            />
                        </div>
                    </div>
                    {analytics.failureRate > 0 && (
                        <div>
                            <div className="flex justify-between text-xs mb-1">
                                <div className="flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3 text-danger" />
                                    <span className="text-muted-foreground">Job Failure Rate</span>
                                </div>
                                <span className="font-medium">{analytics.failureRate.toFixed(1)}%</span>
                            </div>
                            <div className="h-2 bg-default-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-danger rounded-full transition-all"
                                    style={{ width: `${analytics.failureRate}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
}
